// Pure helpers for local media acquisition and Twilio error classification.
// Kept free of React so they can be unit-tested with a mocked createLocalTracks.

// Media states (local camera/mic):
//   idle | requesting-permission | permission-denied | no-device | device-in-use | ready | failed
// Connection states (Twilio room):
//   idle | connecting | connected | reconnecting | disconnected | failed
export const MEDIA_ERROR_STATES = ['permission-denied', 'no-device', 'device-in-use', 'failed'];

// Tried in order until one works. Bare numbers are "ideal" values, but some drivers
// still reject them (NotReadableError / OverconstrainedError), hence the fallbacks.
export const VIDEO_CONSTRAINT_FALLBACKS = [
  { width: 960, height: 720, facingMode: 'user' },
  { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
  {},
];

// Errors that mean "these constraints/this attempt failed, a looser one may work".
const RETRYABLE_CONSTRAINT_ERRORS = ['OverconstrainedError', 'ConstraintNotSatisfiedError', 'NotReadableError', 'TrackStartError', 'AbortError'];

export const classifyMediaError = (error) => {
  if (!error) return null;
  const name = error.name || '';
  if (name === 'InsecureContextError' || name === 'UnsupportedError') return 'failed';
  if (['NotAllowedError', 'PermissionDeniedError', 'SecurityError'].includes(name)) return 'permission-denied';
  if (['NotFoundError', 'DevicesNotFoundError'].includes(name)) return 'no-device';
  if (['NotReadableError', 'TrackStartError', 'AbortError'].includes(name)) return 'device-in-use';
  return 'failed';
};

export const describeMediaError = (error, kind = 'camera') => {
  const status = classifyMediaError(error);
  const device = kind === 'microphone' ? 'microphone' : 'camera';
  switch (status) {
    case 'permission-denied':
      return /system/i.test(error?.message || '')
        ? `Your operating system is blocking the browser from using the ${device}.`
        : `Access to the ${device} was blocked.`;
    case 'no-device':
      return `No ${device} was found on this device.`;
    case 'device-in-use':
      return `The ${device} is being used by another application (or another browser tab).`;
    default:
      if (error?.name === 'InsecureContextError') return 'Camera access requires a secure (https) connection.';
      if (error?.name === 'UnsupportedError') return 'This browser does not support video calls. Please use an up-to-date Chrome, Safari, Edge or Firefox.';
      if (error?.name === 'OverconstrainedError') return `The ${device} does not support the requested settings.`;
      return error?.message || `The ${device} could not be started.`;
  }
};

// Mirror of getUserMedia's preconditions; checked before calling Twilio so the user
// gets an actionable message instead of a TypeError ("cannot read getUserMedia of undefined").
export const checkMediaSupport = (win = typeof window !== 'undefined' ? window : undefined) => {
  if (!win) return null;
  if (win.isSecureContext === false) {
    return { name: 'InsecureContextError', message: 'Camera access requires https (or localhost).' };
  }
  if (!win.navigator?.mediaDevices?.getUserMedia) {
    return { name: 'UnsupportedError', message: 'navigator.mediaDevices.getUserMedia is not available.' };
  }
  return null;
};

export const buildVideoConstraints = (base, deviceId) => {
  if (!deviceId) return { ...base };
  // An explicitly chosen device overrides facingMode.
  const { facingMode, ...rest } = base;
  return { ...rest, deviceId: { exact: deviceId } };
};

export const buildAudioConstraints = (deviceId) => (deviceId ? { deviceId: { exact: deviceId } } : true);

const errorInfo = (error) => (error ? { name: error.name || 'Error', message: error.message || String(error) } : null);

export const acquireVideoTrack = async (createLocalTracks, { deviceId, fallbacks = VIDEO_CONSTRAINT_FALLBACKS } = {}) => {
  let lastError = null;
  for (const base of fallbacks) {
    try {
      const [track] = await createLocalTracks({ audio: false, video: buildVideoConstraints(base, deviceId) });
      return { track, error: null };
    } catch (error) {
      lastError = error;
      if (!RETRYABLE_CONSTRAINT_ERRORS.includes(error?.name)) break;
    }
  }
  return { track: null, error: lastError };
};

export const acquireAudioTrack = async (createLocalTracks, { deviceId } = {}) => {
  try {
    const [track] = await createLocalTracks({ audio: buildAudioConstraints(deviceId), video: false });
    return { track, error: null };
  } catch (error) {
    return { track: null, error };
  }
};

// Returns { video, audio, videoError, audioError }. Never throws.
// 1. One combined request first, so the user sees a single permission prompt.
// 2. If that fails for any reason other than a denial, acquire video (with constraint
//    fallbacks) and audio separately, so a missing/busy mic does not kill the camera.
export const acquireLocalTracks = async (createLocalTracks, { videoDeviceId, audioDeviceId, win } = {}) => {
  const unsupported = checkMediaSupport(win);
  if (unsupported) return { video: null, audio: null, videoError: unsupported, audioError: unsupported };

  try {
    const tracks = await createLocalTracks({
      audio: buildAudioConstraints(audioDeviceId),
      video: buildVideoConstraints(VIDEO_CONSTRAINT_FALLBACKS[0], videoDeviceId),
    });
    return {
      video: tracks.find((t) => t.kind === 'video') || null,
      audio: tracks.find((t) => t.kind === 'audio') || null,
      videoError: null,
      audioError: null,
    };
  } catch (error) {
    if (classifyMediaError(error) === 'permission-denied') {
      return { video: null, audio: null, videoError: error, audioError: error };
    }
  }

  const video = await acquireVideoTrack(createLocalTracks, { deviceId: videoDeviceId });
  // If video was denied at this point there is no point prompting again for audio.
  const audio =
    classifyMediaError(video.error) === 'permission-denied'
      ? { track: null, error: video.error }
      : await acquireAudioTrack(createLocalTracks, { deviceId: audioDeviceId });
  return { video: video.track, audio: audio.track, videoError: video.error, audioError: audio.error };
};

// Result of acquireLocalTracks -> media state. The camera drives the state: if we have
// video we are "ready" even without a mic (that is reported separately as audioError).
export const mediaStateFromResult = ({ video, audio, videoError, audioError }) => {
  if (video) return { status: 'ready', error: null, audioError: audio ? null : errorInfo(audioError) };
  const error = videoError || audioError;
  return { status: classifyMediaError(error) || 'failed', error: errorInfo(error), audioError: audio ? null : errorInfo(audioError) };
};

// Twilio error codes: https://www.twilio.com/docs/api/errors
export const TOKEN_ERROR_CODES = [20101, 20103, 20104, 20105, 20106, 20107, 20157];

export const describeConnectError = (error) => {
  if (!error) return null;
  const code = error.code;
  if (TOKEN_ERROR_CODES.includes(code)) return 'The video access token is invalid or has expired. Press Retry to get a new one.';
  if (code === 53205) return 'You joined this session from another tab or device, so this one was disconnected.';
  if (code === 53105) return 'This session already has the maximum number of participants. Close other tabs with this session open and retry.';
  if (code === 53118) return 'The video room has ended.';
  if ([53000, 53001, 53002].includes(code)) return 'Could not reach the video service. Check your internet connection and retry.';
  if ([53405, 53407, 53408].includes(code)) return 'Could not establish a media connection. A firewall or VPN may be blocking video; try another network.';
  return error.message || 'Could not connect to the video session.';
};

export const connectErrorInfo = (error) => (error ? { name: error.name || 'TwilioError', code: error.code, message: describeConnectError(error) } : null);

// Collapse media + connection into the single status list the UI can switch on.
export const deriveStatus = (mediaStatus, connectionStatus) => {
  if (['reconnecting', 'failed', 'disconnected'].includes(connectionStatus)) return connectionStatus;
  if (mediaStatus === 'requesting-permission' || MEDIA_ERROR_STATES.includes(mediaStatus)) return mediaStatus;
  if (connectionStatus === 'connecting' || connectionStatus === 'connected') return connectionStatus;
  return 'idle';
};

export const detectBrowser = (ua = typeof navigator !== 'undefined' ? navigator.userAgent : '') => {
  const ios = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1);
  const android = /Android/.test(ua);
  if (/Edg\//.test(ua)) return { name: 'edge', ios, android };
  if (/Firefox\/|FxiOS/.test(ua)) return { name: 'firefox', ios, android };
  if (/Chrome\/|CriOS/.test(ua)) return { name: 'chrome', ios, android };
  if (/Safari\//.test(ua)) return { name: 'safari', ios, android };
  return { name: 'other', ios, android };
};

// Short, browser-specific steps to re-enable a blocked camera/microphone.
export const permissionHelp = (browser = detectBrowser()) => {
  if (browser.ios) return 'On iPhone/iPad: tap "aA" in the address bar, then Website Settings, set Camera and Microphone to Allow, and reload. Also check Settings > Safari > Camera.';
  if (browser.android) return 'Tap the icon left of the address bar, open Permissions, allow Camera and Microphone, then press Retry.';
  switch (browser.name) {
    case 'chrome':
    case 'edge':
      return 'Click the camera icon (or the site settings icon) on the left of the address bar, choose Allow for Camera and Microphone, then press Retry. On a Mac also check System Settings > Privacy & Security > Camera.';
    case 'firefox':
      return 'Click the permissions icon on the left of the address bar, remove the "Blocked" camera and microphone entries, then press Retry.';
    case 'safari':
      return 'Open Safari > Settings for This Website, set Camera and Microphone to Allow, then press Retry.';
    default:
      return "Allow camera and microphone access in your browser's site settings, then press Retry.";
  }
};
